/**
 * This directive will be responsible for hiding or showing elements based on the user's permissions
 *
 * The user object comes from the authModule in vuex store
 *
 * The directive should accept an object with the following structure:
 *
 * ```js
 * { app: "On-boarder", actions: ["View Application", "Create Application"] }
 * ```
 *
 * The directive should check if the user has the permission to perform the action in the app
 *
 * If the user does not have the permission, the element should be hidden
 *
 * Do note that the values passed in will be case insensitive. So they will be converted to lower case before comparison
 *
 * Spaces and dashes will not be altered
 *
 * Usage:
 * @example
 *
 * <button v-auth="{app: 'On-Boarder', actions: ['View application']}">Create User</button>
 *
 */

const isBypassMode = import.meta.env.VITE_BYPASS_MODE;

export const AuthorizeDirective = {
  inserted(el, binding, vnode) {
    if (isBypassMode) {
      return;
    }

    const { value } = binding;
    const user = vnode.context?.$store?.getters['authModule/user'];
    // If no user is logged in, remove the element from the DOM
    if (!user) {
      el.style.display = 'none';
    }
    const role = user?.roleObject || {};
    // If the role does not have the permissions, remove the element from the DOM
    if (!role || !role.permissions) {
      el.style.display = 'none';
      return;
    }
    const permissions = role?.permissions || [];
    // fi no permissions are set, remove the element from the DOM
    if (!permissions.length) {
      el.style.display = 'none';
      return;
    }
    // Check if the user has the permission to perform the action on the app
    const hasAppPermission = permissions.find(
      (a) => a?.app?.toLowerCase() === value?.app?.toLowerCase()
    );
    if (!hasAppPermission) {
      el.style.display = 'none';
      return;
    }
    // Check to see if all the items in the actions array are in the permissions array
    const hasPermission = value?.actions?.every((action) => {
      return permissions.find(
        (p) => p?.name?.toLowerCase() === action?.toLowerCase()
      );
    });
    if (!hasPermission) {
      el.style.display = 'none';
      return;
    }
  }
};
